import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import translations from './translations';

const STORAGE_KEY = 'ck-lang';
const DEFAULT_LANG = 'es';
const SUPPORTED = ['es', 'en'];

function getInitialLang() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (e) { /* ignore */ }
  return DEFAULT_LANG;
}

function pathLookup(obj, path) {
  return path.split('.').reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    obj
  );
}

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key, fallback) => {
    const val = pathLookup(translations[lang], key);
    if (val !== undefined) return val;
    const fb = pathLookup(translations[DEFAULT_LANG], key);
    return fb !== undefined ? fb : (fallback !== undefined ? fallback : key);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  return useContext(LanguageContext);
}
